import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteIngestRun,
  bulkDeleteIngestRuns,
  listIngestRuns,
  downloadIngestRun,
} from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { toast } from "../ui/sonner";

// v0.6 P2 cap sweep — run mutation actions extracted from CalculatePanel.jsx.
// Behaviour verbatim: single-run delete, bulk/all delete (skips locked
// files), and download. Refreshes via WorkflowContext when present, else
// the local `listIngestRuns` fallback — and (v0.6.9) surfaces it if that
// refresh itself fails, rather than leaving the deleted row visibly
// unchanged with no explanation.
// v0.6.8 — window.confirm() used to gate both delete actions below.
// The bulk-delete message itself already reads "permanently removes...
// every transaction... Cannot be undone" — an irreversible, high-blast-
// radius action (bulk delete can remove EVERY uploaded run at once)
// deserved more visual weight than a native browser dialog a user can
// blast through by hitting Enter out of habit. `confirmState` drives a
// BlockingMessageDialog-based confirm modal rendered by the caller
// (CalculatePanel.jsx, via calculate/RunActionsConfirmDialog.jsx) —
// the actual delete only runs from that dialog's own Confirm action.
export function useRunActions({ wf, setRuns, setSelected, setError }) {
  const { t } = useTranslation();
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  // v0.6.9 — `strict` refresh does NOT swallow a fetch failure (unlike
  // WorkflowContext's own default-silent `refreshRuns`, used elsewhere
  // for refreshes that aren't the direct consequence of an operator
  // action). A delete IS such a consequence — the operator needs to
  // know if what they see afterward is trustworthy, not silently see
  // the same row with no explanation.
  const refresh = async () => {
    if (wf?.refreshRuns) await wf.refreshRuns({ silent: false });
    else setRuns(await listIngestRuns());
  };

  const REFRESH_FAILED_MSG = () => t("ui.calculate.delete_refresh_failed", {
    defaultValue: "Deleted, but the list didn't refresh — reload the page to confirm.",
  });

  const onDeleteRun = (runId, filename) => {
    setConfirmState({
      message: t("ui.calculate.delete_confirm", { filename }),
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await deleteIngestRun(runId);
          setSelected((prev) => prev.filter((x) => x !== runId));
        } catch (e) {
          const detail = e?.response?.data?.detail;
          const msg = (detail && typeof detail === "object" && detail.message)
            ? detail.message
            : (typeof detail === "string" ? detail : e.message);
          setError(msg);
          return;
        }
        // v0.6.9 — split from the delete's own try/catch above so a
        // refresh failure gets its own, non-misleading message rather
        // than reading as if the delete itself failed.
        try {
          await refresh();
        } catch {
          setError(REFRESH_FAILED_MSG());
        }
      },
    });
  };

  const doBulkDelete = (ids, confirmMsg) => {
    if (!ids || ids.length === 0) return;
    setConfirmState({ message: confirmMsg, onConfirm: () => runBulkDelete(ids) });
  };

  const runBulkDelete = async (ids) => {
    setConfirmState(null);
    setBulkDeleting(true);
    setError("");
    try {
      let res;
      try {
        res = await bulkDeleteIngestRuns(ids);
      } catch (e) {
        setError(errorMessage(e));
        return;
      }
      const deleted = res?.deleted_count ?? 0;
      const skipped = res?.skipped_count ?? 0;
      setSelected([]);
      if (skipped > 0) {
        toast.success(
          t("ui.calculate.bulk_delete_done", {
            deleted, skipped,
            defaultValue: `Deleted ${deleted} · skipped ${skipped}`,
          }),
          {
            description: t("ui.calculate.bulk_delete_skipped_reason", {
              defaultValue: "Skipped files are locked by a signed/frozen calculation.",
            }),
            duration: 8000,
          },
        );
      } else {
        toast.success(t("ui.calculate.bulk_delete_done_clean", {
          deleted, skipped,
          defaultValue: `Deleted ${deleted} upload(s).`,
        }));
      }
      // v0.6.9 — split from the delete's own try/catch above so a
      // refresh failure gets its own, non-misleading message rather
      // than reading as if the bulk-delete itself failed.
      try {
        await refresh();
      } catch {
        setError(REFRESH_FAILED_MSG());
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const onDownloadRun = async (runId, filename) => {
    const res = await downloadIngestRun(runId, filename);
    if (!res.ok) {
      toast.error(res.error || "Download failed");
    }
  };

  return {
    bulkDeleting, onDeleteRun, doBulkDelete, onDownloadRun,
    confirmState, dismissConfirm: () => setConfirmState(null),
  };
}
